import { useState } from 'react'
import { cn } from '@/lib/utils'
import { useConnectionStore } from '@/stores/connectionStore'
import { useAIStore } from '@/stores/aiStore'
import {
  GitBranch,
  FileUp,
  Settings,
  Sparkles,
  BarChart3,
  FolderTree,
  LayoutGrid,
  Compass,
  Network,
  MessageSquare,
  FolderOpen,
  ChevronRight,
  ChevronDown,
  Users,
  HelpCircle,
  FileEdit,
  Bot,
  BookOpen,
  FlaskConical,
  Brain,
  Wrench,
  Globe,
  Upload,
  FolderOutput,
  PanelLeftClose,
  PanelLeft,
  GitMerge,
  type LucideIcon,
} from 'lucide-react'

interface Project {
  id: string
  name: string
  slug: string
  role: string
}

interface SidebarProps {
  activeView: string
  onViewChange: (view: string) => void
  currentProject?: Project | null
  onSwitchProject?: () => void
}

interface NavItem {
  id: string
  label: string
  icon: LucideIcon
}

interface NavSubSection {
  label: string
  items: NavItem[]
}

interface NavSection {
  id: string
  label: string
  icon: LucideIcon
  items?: NavItem[]
  subSections?: NavSubSection[]
}

type NavEntry = NavItem | NavSection

const NAV_STRUCTURE: NavEntry[] = [
  // Workspace - standalone
  { id: 'workspace', label: 'Workspace', icon: LayoutGrid },

  // Sources section
  {
    id: 'sources',
    label: 'Sources',
    icon: Upload,
    items: [
      { id: 'interviewer', label: 'Interviews', icon: MessageSquare },
      { id: 'docs', label: 'Docs', icon: FlaskConical },
      { id: 'web', label: 'Web', icon: Globe },
    ],
  },

  // Deliverables section
  {
    id: 'deliverables',
    label: 'Deliverables',
    icon: FolderOutput,
    items: [
      { id: 'canvas', label: 'Canvas', icon: LayoutGrid },
      { id: 'smartdocs', label: 'Smart Docs', icon: FileEdit },
    ],
  },

  // Intelligence section
  {
    id: 'intelligence',
    label: 'Intelligence',
    icon: Brain,
    items: [
      { id: 'analysis', label: 'Validation', icon: BarChart3 },
      { id: 'dedup', label: 'Dedup', icon: GitMerge },
      { id: 'ontology', label: 'Ontology', icon: Network },
      { id: 'graph', label: 'Graph', icon: GitBranch },
      { id: 'tree', label: 'Tree View', icon: FolderTree },
      { id: 'knowledge', label: 'Knowledge Base', icon: BookOpen },
    ],
  },

  // Advanced section
  {
    id: 'advanced',
    label: 'Advanced',
    icon: Wrench,
    items: [
      { id: 'discovery', label: 'Discovery', icon: Compass },
      { id: 'discovery-assistant', label: 'AI Coach', icon: Bot },
      { id: 'pdf', label: 'Documents', icon: FileUp },
      { id: 'crm', label: 'Customers', icon: Users },
      { id: 'reports', label: 'Reports', icon: BarChart3 },
    ],
  },
]

function isSection(entry: NavEntry): entry is NavSection {
  return 'items' in entry || 'subSections' in entry
}

// Helper to check if any item in a section is active
function hasActiveItem(section: NavSection, activeView: string): boolean {
  const itemActive = section.items?.some(item => item.id === activeView) ?? false
  const subSectionActive = section.subSections?.some(sub =>
    sub.items.some(item => item.id === activeView)
  ) ?? false
  return itemActive || subSectionActive
}

export function Sidebar({ activeView, onViewChange, currentProject, onSwitchProject }: SidebarProps) {
  const { isConnected, isConnecting } = useConnectionStore()
  const { isConfigured: aiConfigured } = useAIStore()

  // Track collapsed state
  const [isCollapsed, setIsCollapsed] = useState(false)

  // Track expanded sections - only expand sections that contain the active view
  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => {
    const initial = new Set<string>()
    NAV_STRUCTURE.forEach(entry => {
      if (isSection(entry) && hasActiveItem(entry, activeView)) {
        initial.add(entry.id)
      }
    })
    return initial
  })

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev)
      if (next.has(sectionId)) {
        next.delete(sectionId)
      } else {
        next.add(sectionId)
      }
      return next
    })
  }

  const renderNavItem = (item: NavItem, indent: number = 0) => {
    const Icon = item.icon
    const isActive = activeView === item.id
    return (
      <li key={item.id}>
        <button
          onClick={() => onViewChange(item.id)}
          title={isCollapsed ? item.label : undefined}
          className={cn(
            'w-full flex items-center gap-3 h-9 text-sm rounded-lg transition-colors',
            isCollapsed ? 'justify-center px-2' : 'px-3',
            isActive
              ? 'bg-solution/10 text-solution font-medium'
              : 'text-text-secondary hover:bg-warm-200/50 hover:text-text'
          )}
          style={isCollapsed ? undefined : { paddingLeft: `${12 + indent * 12}px` }}
        >
          <Icon className={cn(
            'w-4 h-4 flex-shrink-0',
            isActive ? 'text-solution' : 'text-text-muted'
          )} />
          {!isCollapsed && <span>{item.label}</span>}
        </button>
      </li>
    )
  }

  const renderSection = (section: NavSection) => {
    const isExpanded = expandedSections.has(section.id)
    const hasActive = hasActiveItem(section, activeView)
    const Icon = section.icon

    // In collapsed mode, show only the section icon (clickable to expand sidebar)
    if (isCollapsed) {
      return (
        <li key={section.id} className="mt-2">
          <button
            onClick={() => setIsCollapsed(false)}
            title={section.label}
            className={cn(
              'w-full flex items-center justify-center h-9 px-2 text-sm rounded-lg transition-colors',
              hasActive ? 'text-text font-medium bg-warm-200/50' : 'text-text-secondary hover:bg-warm-200/50 hover:text-text'
            )}
          >
            <Icon className={cn(
              'w-4 h-4 flex-shrink-0',
              hasActive ? 'text-solution' : 'text-text-muted'
            )} />
          </button>
        </li>
      )
    }

    return (
      <li key={section.id} className="mt-2">
        <button
          onClick={() => toggleSection(section.id)}
          className={cn(
            'w-full flex items-center gap-3 h-9 px-3 text-sm rounded-lg transition-colors',
            hasActive ? 'text-text font-medium' : 'text-text-secondary hover:bg-warm-200/50 hover:text-text'
          )}
        >
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 flex-shrink-0 text-text-muted" />
          ) : (
            <ChevronRight className="w-4 h-4 flex-shrink-0 text-text-muted" />
          )}
          <Icon className="w-4 h-4 flex-shrink-0 text-text-muted" />
          <span>{section.label}</span>
        </button>

        {isExpanded && (
          <ul className="mt-1 space-y-0.5">
            {/* Render sub-sections first */}
            {section.subSections?.map(subSection => (
              <li key={subSection.label} className="mt-1">
                <div className="px-3 py-1 text-xs font-medium text-text-muted uppercase tracking-wide" style={{ paddingLeft: '36px' }}>
                  {subSection.label}
                </div>
                <ul className="space-y-0.5">
                  {subSection.items.map(item => renderNavItem(item, 2))}
                </ul>
              </li>
            ))}
            {/* Render direct items */}
            {section.items?.map(item => renderNavItem(item, 1))}
          </ul>
        )}
      </li>
    )
  }

  return (
    <aside className={cn(
      'h-full bg-warm-100 flex flex-col flex-shrink-0 rounded-l-2xl transition-all duration-200',
      isCollapsed ? 'w-16' : 'w-56'
    )}>
      {/* Logo + Collapse Toggle */}
      <div className={cn(
        'h-16 flex items-center',
        isCollapsed ? 'px-3 justify-center' : 'px-4 gap-3'
      )}>
        <div className="w-8 h-8 rounded-lg bg-solution flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-4 h-4 text-white" />
        </div>
        {!isCollapsed && (
          <span className="text-base font-semibold text-text flex-1">Intellispot</span>
        )}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-warm-200/50 text-text-muted hover:text-text transition-colors"
        >
          {isCollapsed ? (
            <PanelLeft className="w-4 h-4" />
          ) : (
            <PanelLeftClose className="w-4 h-4" />
          )}
        </button>
      </div>

      {/* Current Project */}
      {currentProject && onSwitchProject && (
        <div className={cn('pb-2', isCollapsed ? 'px-2' : 'px-3')}>
          <button
            onClick={onSwitchProject}
            title={isCollapsed ? currentProject.name : undefined}
            className={cn(
              'w-full flex items-center bg-warm-200/50 hover:bg-warm-200 rounded-lg transition-colors group',
              isCollapsed ? 'justify-center p-2' : 'gap-2 px-3 py-2'
            )}
          >
            <FolderOpen className="w-4 h-4 text-solution flex-shrink-0" />
            {!isCollapsed && (
              <>
                <div className="flex-1 text-left min-w-0">
                  <div className="text-sm font-medium text-text truncate">
                    {currentProject.name}
                  </div>
                  <div className="text-xs text-text-muted truncate">
                    {currentProject.role}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-text-muted group-hover:text-text" />
              </>
            )}
          </button>
        </div>
      )}

      {/* Navigation */}
      <nav className={cn('flex-1 py-4 overflow-y-auto', isCollapsed ? 'px-2' : 'px-3')}>
        <ul className="space-y-0.5">
          {NAV_STRUCTURE.map(entry => {
            if (isSection(entry)) {
              return renderSection(entry)
            }
            return renderNavItem(entry, 0)
          })}
        </ul>
      </nav>

      {/* Footer */}
      <div className={cn('pb-6 space-y-1', isCollapsed ? 'px-2' : 'px-3')}>
        <button
          onClick={() => onViewChange('help')}
          title={isCollapsed ? 'Help' : undefined}
          className={cn(
            'w-full flex items-center h-10 text-sm rounded-lg transition-colors',
            isCollapsed ? 'justify-center px-2' : 'gap-3 px-3',
            activeView === 'help'
              ? 'bg-solution/10 text-solution font-medium'
              : 'text-text-secondary hover:bg-warm-200/50 hover:text-text'
          )}
        >
          <HelpCircle className={cn(
            'w-[18px] h-[18px] flex-shrink-0',
            activeView === 'help' ? 'text-solution' : 'text-text-muted'
          )} />
          {!isCollapsed && <span>Help</span>}
        </button>
        <button
          onClick={() => onViewChange('settings')}
          title={isCollapsed ? 'Settings' : undefined}
          className={cn(
            'w-full flex items-center h-10 text-sm rounded-lg transition-colors',
            isCollapsed ? 'justify-center px-2' : 'gap-3 px-3',
            activeView === 'settings'
              ? 'bg-solution/10 text-solution font-medium'
              : 'text-text-secondary hover:bg-warm-200/50 hover:text-text'
          )}
        >
          <Settings className={cn(
            'w-[18px] h-[18px] flex-shrink-0',
            activeView === 'settings' ? 'text-solution' : 'text-text-muted'
          )} />
          {!isCollapsed && <span>Settings</span>}
        </button>

        {/* Status indicator */}
        <div className={cn(
          'mt-4 flex items-center',
          isCollapsed ? 'justify-center px-0' : 'px-3 gap-2'
        )}>
          <div className={cn(
            'w-2 h-2 rounded-full',
            isConnecting ? 'bg-warning animate-pulse' :
            isConnected ? 'bg-business' : 'bg-text-muted'
          )} />
          {!isCollapsed && (
            <>
              <span className="text-xs text-text-muted">
                {isConnecting ? 'Connecting...' :
                 isConnected ? 'Connected' : 'Offline'}
              </span>
              {aiConfigured && (
                <>
                  <span className="text-xs text-text-muted">·</span>
                  <span className="text-xs text-business">AI</span>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </aside>
  )
}
